import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Link,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Switch,
} from '@mui/material';
import {
  Global,
  People,
  CallCalling,
  MessageText1,
  Location,
  Sms,
  Share,
  MoreCircle,
  RepeatCircle,
  Edit2,
  Setting2,
  Lock,
  Eye,
  Trash,
} from 'iconsax-react';
import styles from './cards.module.scss';
import {
  getNameFromProfile,
  getNameFromProfileOptions,
  getPhoneNumberWihKey,
} from '../../utils/profile-utils';
import NFCIcon from '../../icons/nfc.png';
import ShareModal from './share-modal';
import QRModal from './qr-modal';
import { SocialIcon } from 'react-social-icons';
import DeleteCardModal from './delete-card-modal';
import ViewImgModal from '../../my-data/category/view-img-modal';

const Card = ({ card, contact, settings, uiStore }) => {
  const [flipCard, setFlipCard] = useState(false);
  const [name, setName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [company, setCompany] = useState('');
  const [location, setLocation] = useState({ lat: 10, lng: 10, zoom: 1 });
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPublic, setPublic] = useState(card?.isPublic);
  const [allowFollowing, setFollow] = useState(card?.allowFollowing);
  const [allowSyncing, setSync] = useState(card?.allowSyncing);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImgModal, setShowImgModal] = useState(false);

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [anchorSettings, setAnchorSettings] = useState(null);
  const openSettings = Boolean(anchorSettings);
  const handleSettingsClick = (event) => {
    setAnchorSettings(event.currentTarget);
  };
  const handleSettingsClose = () => {
    setAnchorSettings(null);
  };

  useEffect(() => {
    let lang = '';
    if (settings) {
      if (card?.langs?.includes(settings.lang)) lang = settings.lang;
      else lang = card?.langs[0];

      setName(
        getNameFromProfileOptions(card, settings.nameFormat, settings.lang)
      );
    } else {
      setName(getNameFromProfile(card));
    }

    if (card.JobTitle)
      setJobTitle(card.JobTitle.infoValue[lang] || card.JobTitle.infoValue);

    if (card.Field)
      setJobTitle(card.Field.infoValue[lang] || card.Field.infoValue);

    if (card.Company)
      setCompany(card.Company.infoValue[lang] || card.Company.infoValue);

    if (card.Department)
      setDepartment(
        card.Department.infoValue[lang] || card.Department.infoValue
      );

    if (card.Location) {
      const newLocation = JSON.parse(card.Location.infoValue);
      setLocation(newLocation);
    }

    if (card.Nickname && card.isBasic) setJobTitle(card.Nickname.infoValue);
    if (card.Nickname && !card.JobTitle && !card.Field) setJobTitle(card.Nickname.infoValue);
  }, [card, settings]);

  const handleSettingsChange = (e, field) => {
    const checked = !!e.target.checked;

    const body = {
      isPublic,
      allowFollowing,
      allowSyncing,
    };

    if (field === 'sync') {
      setSync(checked);
      body.allowSyncing = checked;
    } else if (field === 'follow') {
      setFollow(checked);
      body.allowFollowing = checked;
    } else {
      setPublic(checked);
      body.isPublic = checked;
    }

    uiStore.updateCardSettings(card?._id, body);
  };

  return (
    <Box className={flipCard ? [styles.card, styles.cardFlipped] : styles.card}>
      <Box className={styles.cardInner}>
        <Box className={styles.cardFront}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box className={styles.cardPublic}>
              {isPublic ? (
                <Tooltip title="Public">
                  <Global size="24" color="black" variant="Bold" />
                </Tooltip>
              ) : (
                <Tooltip title="Private">
                  <Lock size="24" color="black" variant="Bulk" />
                </Tooltip>
              )}
              <Typography fontSize="14px">{card.name}</Typography>
            </Box>
            <Box className={styles.cardHeader}>
              <People size="24" variant="Bulk" />
              <Typography fontSize="12px" fontWeight="500">
                {card.connections}
              </Typography>
            </Box>
          </Box>

          <Box className={styles.cardInfo}>
            <Box className={styles.cardImg}>
              <Button onClick={() => setShowImgModal(true)} sx={{p: 0, overflow: 'hidden', minWidth: 'fit-content'}}>
                <img src={card.image} alt="Pesonal"></img>
              </Button>
            </Box>
            <Box className={styles.cardTitle}>
              <Link
                href={!name?.startsWith('User') ? '/card/' + card._id : '/edit-card/' + card._id + '?category=Name'}
                sx={{ textDecorationColor: 'rgba(29, 23, 97, 0)' }}
              >
                <Typography
                  fontSize="16px"
                  fontWeight="600"
                  color="primary"
                  className={styles.cardName}
                >
                  {name?.startsWith('User') ? <Link> Select Display Name </Link> : name}
                </Typography>
              </Link>
              <Typography fontSize="12px" fontWeight="500">
                {jobTitle}
              </Typography>
              <Typography fontSize="12px" fontWeight="500">
                {department && department}
                {department && company && ' - '}
                {company && '' + company}
              </Typography>
            </Box>
          </Box>

          <Box className={styles.cardActions}>
            <Box>
              {card.Number && (
                <Tooltip title="Call">
                  <IconButton
                    href={
                      'tel:' + getPhoneNumberWihKey(card?.Number)
                    }
                  >
                    <CallCalling size="24" variant="Bulk" />
                  </IconButton>
                </Tooltip>
              )}
              {card.Number?.countryCode && (
                <Tooltip title="Text">
                  <IconButton
                    href={
                      'sms:' + getPhoneNumberWihKey(card?.Number)
                    }
                  >
                    <MessageText1 size="24" variant="Bulk" />
                  </IconButton>
                </Tooltip>
              )}
              {card.Email && (
                <Tooltip title="Email">
                  <IconButton href={'mailto:' + card.Email.infoValue}>
                    <Sms size="24" variant="Bulk" />
                  </IconButton>
                </Tooltip>
              )}
              {card.Location && (
                <Tooltip title="Location">
                  <IconButton
                    href={`https://www.google.com/maps/search/?api=1&query=${location?.lat},${location?.lng}`}
                    target="_blank"
                    rel='noopener noreferrer'
                  >
                    <Location size="24" variant="Bulk" />
                  </IconButton>
                </Tooltip>
              )}
              {card.Website && (
                <Tooltip title="Website">
                  <IconButton
                    href={card.Website.infoValue}
                    target="_blank"
                    rel='noopener noreferrer'
                  >
                    <Global size="24" variant="Bulk" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box className={styles.cardSecActions}>
              {!flipCard && !!card.socials.length && (
                <>
                  <Tooltip title="Socials">
                    <IconButton
                      id="basic-button"
                      aria-controls={open ? 'basic-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? 'true' : undefined}
                      onClick={handleClick}
                    >
                      <Share size="24" variant="Bulk" />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        borderRadius: '8px',
                        width: '150px',
                        boxShadow: '0px 0px 15px 0px rgba(0,0,0,0.07)',
                        paddingTop: 0,
                      },
                    }}
                  >
                    {card.socials.map(social =>
                      <MenuItem key={social._id} href={social.infoValue} component='a' rel='noopener noreferrer' target="_blank">
                        <ListItemIcon>
                          <SocialIcon network={social.section.name.toLowerCase()} style={{ height: 18, width: 18 }} />
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '14px' }}
                        >
                          {social.section.name}
                        </ListItemText>
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}

              <Fade in={!flipCard}>
                <Tooltip title="Options">
                  <IconButton onClick={() => setFlipCard(true)}>
                    <MoreCircle size="24" variant="Bulk" />
                  </IconButton>
                </Tooltip>
              </Fade>
            </Box>
          </Box>
        </Box>

        <Box className={styles.cardBack}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box className={styles.cardPublic}>
              {isPublic ? (
                <Tooltip title="Public">
                  <Global size="24" color="black" variant="Bulk" />
                </Tooltip>
              ) : (
                <Tooltip title="Private">
                  <Lock size="24" color="black" variant="Bulk" />
                </Tooltip>
              )}
              <Typography fontSize="14px">{card.name}</Typography>
            </Box>
            <Box className={styles.cardHeader}>
              <Tooltip title="Settings">
                <Button
                  variant="contained"
                  color="white"
                  sx={{ minWidth: 'fit-content' }}
                  aria-controls={openSettings ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={openSettings ? 'true' : undefined}
                  onClick={handleSettingsClick}
                >
                  <Setting2 size="24" variant="Bulk" />
                </Button>
              </Tooltip>
              <Menu
                id="basic-menu"
                anchorEl={anchorSettings}
                open={openSettings}
                onClose={handleSettingsClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    borderRadius: '8px',
                    width: '150px',
                    boxShadow: '0px 0px 15px 0px rgba(0,0,0,0.07)',
                    paddingTop: 0,
                  },
                }}
              >
                <MenuItem>
                  <ListItemText primaryTypographyProps={{ fontSize: '12px' }}>
                    Public
                  </ListItemText>
                  <Switch
                    color="secondary"
                    onChange={(e) => handleSettingsChange(e, 'public')}
                    checked={isPublic}
                    size="small"
                  />
                </MenuItem>
                {!card?.isBasic && isPublic &&
                  <>
                    <MenuItem>
                      <ListItemText primaryTypographyProps={{ fontSize: '12px' }}>
                        Sync
                      </ListItemText>
                      <Switch
                        color="secondary"
                        onChange={(e) => handleSettingsChange(e, 'sync')}
                        checked={allowSyncing}
                        size="small"
                      />
                    </MenuItem>
                  </>
                }
                <MenuItem href={'/edit-card/' + card?._id} component='a'>
                  <ListItemText primaryTypographyProps={{ fontSize: '12px' }}>
                    More
                  </ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
          <Box className={styles.cardBackActions}>
            <ShareModal cardId={card?._id} />
            <QRModal cardId={card?._id} />
            <Button color="greyPrimary">
              <img src={NFCIcon} alt="Share" />
              NFC
            </Button>
          </Box>
          <Box className={styles.cardActions}>
            <Box>
              <Tooltip title="View">
                <IconButton href={'/card/' + card?._id}>
                  <Eye size="24" variant="Bulk" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton href={'/edit-card/' + card?._id}>
                  <Edit2 size="24" variant="Bulk" />
                </IconButton>
              </Tooltip>
              {!card.isBasic &&
                <Tooltip title="Delete">
                  <IconButton onClick={() => setShowDeleteModal(true)}>
                    <Trash size="24" variant="Bulk" />
                  </IconButton>
                </Tooltip>
              }
            </Box>
            <Box>
              <Tooltip title="Back">
                <IconButton onClick={() => setFlipCard(false)}>
                  <RepeatCircle size="24" variant="Bulk" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>

      <DeleteCardModal uiStore={uiStore} show={showDeleteModal} setShow={setShowDeleteModal} card={card} />
      <ViewImgModal img={card?.image} show={showImgModal} setShow={setShowImgModal} />
    </Box>
  );
};

export default Card;
